import type { IStockService, IInventoryEventEmitter } from './interfaces';
import { Quantity } from '../value-objects/Quantity';
import type { Movement } from '../shared/types';
import { MovementTypes, MovementStatusTypes } from '../shared/types';

export class StockService implements IStockService {
  constructor(
    private readonly getMovements: (productId: string, warehouseId?: string) => Promise<Movement[]>,
    private readonly eventEmitter: IInventoryEventEmitter,
    private readonly lowStockThreshold: number = 10
  ) {}

  async getAvailableStock(productId: string, warehouseId?: string): Promise<Quantity> {
    const movements = await this.getMovements(productId, warehouseId);
    const completedMovements = movements.filter(m => m.status === 'COMPLETED');

    let currentStock = 0;
    let unit = ''; // Set from first movement

    for (const movement of completedMovements) {
      if (!unit && movement.quantity.unit) {
        unit = movement.quantity.unit;
      }

      switch (movement.movementType) {
        case MovementTypes.IN:
          currentStock += movement.quantity.value;
          break;
        case MovementTypes.OUT:
          currentStock -= movement.quantity.value;
          break;
        case MovementTypes.TRANSFER:
          if (warehouseId) {
            if (movement.fromLocationId === warehouseId) {
              currentStock -= movement.quantity.value;
            } else if (movement.toLocationId === warehouseId) {
              currentStock += movement.quantity.value;
            }
          }
          break;
        case MovementTypes.ADJUSTMENT:
          // For adjustments, we directly set the stock level
          currentStock = movement.quantity.value;
          break;
      }
    }

    const stock = Quantity.create(Math.max(0, currentStock), unit || 'units');

    // Check for low stock condition
    if (warehouseId && currentStock <= this.lowStockThreshold) {
      if (currentStock === 0) {
        this.eventEmitter.emitStockOut(productId, warehouseId);
      } else {
        this.eventEmitter.emitLowStock(productId, warehouseId, stock);
      }
    }

    return stock;
  }

  async getReservedStock(productId: string, warehouseId?: string): Promise<Quantity> {
    const movements = await this.getMovements(productId, warehouseId);
    const pendingOutMovements = movements.filter(m => 
      m.status === MovementStatusTypes.PENDING && 
      (m.movementType === MovementTypes.OUT || 
       (m.movementType === MovementTypes.TRANSFER && m.fromLocationId === warehouseId))
    );

    if (pendingOutMovements.length === 0) {
      return Quantity.zero('units');
    }

    return pendingOutMovements.reduce((total, movement) => {
      return total.add(Quantity.create(movement.quantity.value, movement.quantity.unit));
    }, Quantity.zero(pendingOutMovements[0].quantity.unit));
  }

  async calculateProjectedStock(productId: string, warehouseId?: string): Promise<Quantity> {
    const [available, reserved] = await Promise.all([
      this.getAvailableStock(productId, warehouseId),
      this.getReservedStock(productId, warehouseId)
    ]);

    return available.subtract(reserved);
  }

  async isStockAvailable(
    productId: string,
    quantity: Quantity,
    warehouseId?: string
  ): Promise<boolean> {
    const available = await this.calculateProjectedStock(productId, warehouseId);
    return available.value >= quantity.value && available.unit === quantity.unit;
  }
}